import * as React from 'react'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import type { Block, NumberBlock } from '@basehub/data/replicache/index'
import * as PropertiesPanel from '../../properties-panel'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useTabsStore } from '../../tabs/store'

export const NumberBlockPropertiesPanel = ({
  block,
  parent,
  path,
}: {
  block: NumberBlock
  parent: Block
  path: string
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  return (
    <PropertiesPanel.PropertiesContainer>
      <PropertiesPanel.Group title="Constraints" spacing="tight">
        <PropertiesPanel.BooleanOption
          name="Is required"
          value={block.constraints.isRequired}
          id={`el-${sourceBlockId}-pp-is-required`}
          mutate={async (v) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                constraints: { isRequired: v },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        <PropertiesPanel.TextOption
          name="Min"
          id={`el-${sourceBlockId}-pp-min-value`}
          value={block.constraints.minValue?.toString() ?? ''}
          type="number"
          className="!mt-2.5"
          horizontal
          placeholder="Unset"
          inputProps={{ className: 'max-w-[230px]' }}
          mutate={async (v) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                constraints: { minValue: v ? parseInt(v) : null },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-min-value`),
                  { op: 'updateBlock', args: params },
                  {
                    op: 'customWindowEvent',
                    args: {
                      name: `${sourceBlockId}-pp-min-value`,
                      detail: { action: 'sync' },
                    },
                  },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-min-value`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                  {
                    op: 'customWindowEvent',
                    args: {
                      name: `${sourceBlockId}-pp-min-value`,
                      detail: { action: 'sync' },
                    },
                  },
                ],
              })
            }
          }}
        />
        <PropertiesPanel.TextOption
          name="Max"
          id={`el-${sourceBlockId}-pp-max-value`}
          type="number"
          className="!mt-2.5"
          horizontal
          placeholder="Unset"
          inputProps={{ className: 'max-w-[230px]' }}
          value={block.constraints.maxValue?.toString() ?? ''}
          mutate={async (v) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                constraints: { maxValue: v ? parseInt(v) : null },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-max-value`),
                  { op: 'updateBlock', args: params },
                  {
                    op: 'customWindowEvent',
                    args: {
                      name: `${sourceBlockId}-pp-max-value`,
                      detail: { action: 'sync' },
                    },
                  },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-max-value`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                  {
                    op: 'customWindowEvent',
                    args: {
                      name: `${sourceBlockId}-pp-max-value`,
                      detail: { action: 'sync' },
                    },
                  },
                ],
              })
            }
          }}
        />
      </PropertiesPanel.Group>

      <PropertiesPanel.CommonSearchConfigGroup
        block={block}
        parent={parent}
        path={path}
      />
    </PropertiesPanel.PropertiesContainer>
  )
}
